import type { AlertGate, AlertGateMetric } from '@beaded/models'

const VALID_METRICS: readonly AlertGateMetric[] = [
  'groundTemp',
  'airTemp',
  'surfaceTemp',
  'battery',
]

function isValidMetric(x: unknown): x is AlertGateMetric {
  return VALID_METRICS.some((m) => m === x)
}

export function isAlertGate(x: unknown): x is AlertGate {
  if (!x || typeof x !== 'object') return false
  const g: Record<string, unknown> = { ...x }
  return (
    isValidMetric(g.metric) &&
    (g.operator === 'gt' || g.operator === 'lt') &&
    typeof g.value === 'number' &&
    Number.isFinite(g.value) &&
    (g.severity === 'warn' || g.severity === 'offline')
  )
}

// A gate whose metric this client doesn't recognise (e.g. written by a newer
// app version). These are preserved verbatim on save. Gates with a known
// metric but an otherwise invalid shape are corrupt and get dropped instead.
function isUnknownMetricGate(x: unknown): boolean {
  if (!x || typeof x !== 'object') return false
  const g: Record<string, unknown> = { ...x }
  return typeof g.metric === 'string' && !isValidMetric(g.metric)
}

// Cap preserved unknown-metric gates to avoid unbounded growth from
// repeated partial saves against a polluted server value.
const MAX_UNKNOWN_GATES = 20

/**
 * Merge client-state gates back into the server's gate list for saving.
 *
 * Only client-known gates are held in React state, so the server list is
 * rebuilt in its original order: unknown-metric gates stay in place (capped),
 * valid slots are refilled from client state, corrupt known-metric gates are
 * dropped, and genuinely new client gates are appended. An unchanged save
 * therefore produces no spurious patch ops.
 */
export function mergeGatesForSave(
  clientGates: AlertGate[],
  rawServerGates: unknown[],
): unknown[] {
  const clientGateQueue = [...clientGates]
  const gatesToSave: unknown[] = []
  let unknownCount = 0
  for (const g of rawServerGates) {
    if (isUnknownMetricGate(g)) {
      if (unknownCount < MAX_UNKNOWN_GATES) {
        gatesToSave.push(g)
        unknownCount++
      }
    } else if (isAlertGate(g)) {
      const next = clientGateQueue.shift()
      if (next !== undefined) gatesToSave.push(next)
      // No client gate left for this slot: it was deleted client-side.
    }
    // Known metric but invalid shape: corrupt -- drop it.
  }
  gatesToSave.push(...clientGateQueue)
  return gatesToSave
}
